import {
  Component,
  ElementRef,
  forwardRef,
  input,
  InputSignal,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { BaseRadioDirective } from '../base/base-radio.directive';
import { generateUUID } from '../../../utils/generate-uuid.util';
import { PrimitiveTypes } from '@angular/cli/src/analytics/analytics-parameters';

export interface RadioCardSubitem {
  value: PrimitiveTypes;
  label: string;
}

@Component({
  selector: 'app-radio-card',
  templateUrl: './radio-card.component.html',
  imports: [ReactiveFormsModule, FormsModule, NgClass],
  styleUrl: './radio-card.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioCardComponent),
      multi: true
    }
  ]
})
export class RadioCardComponent
  extends BaseRadioDirective
  implements ControlValueAccessor
{
  private readonly radioControl: Signal<ElementRef<HTMLInputElement>> =
    viewChild.required<ElementRef<HTMLInputElement>>('radioControl');

  icon: InputSignal<string> = input<string>('');
  description: InputSignal<string> = input<string>('');
  danger: InputSignal<string> = input<string>('');

  // Subitems
  protected readonly subitemName: string = generateUUID();
  subitems: InputSignal<RadioCardSubitem[]> = input<RadioCardSubitem[]>([]);
  subitemValue: WritableSignal<PrimitiveTypes> = signal<PrimitiveTypes>('');

  onSubitemValueChange(value: PrimitiveTypes) {
    this.subitemValue.set(value);
    this.onChange(this.isArray() ? [value] : value);
  }

  override onValueChange() {
    this.value.set(this.radioValue());

    if (this.subitems().length > 0) {
      this.onSubitemValueChange(this.subitems()[0].value);
    } else {
      this.onChange(this.isArray() ? [this.radioValue()] : this.radioValue());
    }
  }

  override writeValue(outsideValue: PrimitiveTypes | PrimitiveTypes[]): void {
    const value: PrimitiveTypes = Array.isArray(outsideValue)
      ? outsideValue[0]
      : outsideValue;

    this.value.set(value);

    const isSubitemActive: boolean = this.isSubitemActive(value);
    if (isSubitemActive) {
      this.subitemValue.set(value);
      this.radioControl()?.nativeElement.click();
    }
  }

  private isSubitemActive(
    value: PrimitiveTypes,
    subitems: RadioCardSubitem[] = this.subitems()
  ): boolean {
    return !!subitems.find((subitem: RadioCardSubitem): boolean => {
      return subitem.value == value;
    });
  }
}
